import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import AdminShell from "../AdminShell";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import "./admin-analytics.css";

/**
 * RAdminAnalytics — /admin/analytics
 *
 * Redesigned Analytics & Reporting screen, visuals per
 * design-ref/admin/Analytics.html; functionality replicated 1:1 from the
 * legacy frontend/src/pages/AdminAnalyticsReportingPage.js:
 *   - GET /api/admin/bookings?start_date&end_date   → KPI tiles + charts
 *   - GET /api/admin/tours/log?start_date&end_date[&source] → tour log
 *   - GET /api/admin/manual-tours/analytics          → per-source stats tiles
 *   - GET /api/admin/tours/log/export-pdf[&source]   → PDF blob download
 *   - client-side summary CSV + per-source tour-log CSV exports
 *   - 30s silent auto-refresh of the analytics data (interval reset on
 *     date-range change), cache-busted no-store requests
 *   - date presets (Last 30/7 days + This Week … All Time), source filter,
 *     tour-time distribution recomputed from the tour log (online + walk-in)
 * Legacy error banners are surfaced as toasts per the redesign conventions.
 */

/* ---------------------------------------------------------------- utils -- */

const isoDate = (d) => d.toISOString().split("T")[0];

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(amount);

/* KPI totals match the design's whole-dollar figures ($18,240) */
const formatCurrencyWhole = (amount) =>
  new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(amount);

/* "1 Jul 2026" — same formatter the legacy page used in the log table/CSV */
const formatDateDisplay = (dateString) => {
  const date = new Date(dateString + "T00:00:00");
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

/* Legacy no-cache request headers (cache-busted, no-store) */
const NO_CACHE_HEADERS = {
  "Cache-Control": "no-cache, no-store, must-revalidate",
  Pragma: "no-cache",
};

/* design-token chart colors (donut: teal / blue / ember) */
const DIST_COLORS = {
  "10:00": "#1bab88",
  "14:00": "#2596c9",
  "16:00": "#f15a24",
};

/* per-row source tags — colors verbatim from the design table */
const SOURCE_TAGS = {
  online: { label: "ONLINE", color: "#0ea5e9" },
  oasis: { label: "OASIS", color: "#10b981" },
  noble: { label: "NOBLE", color: "#0ea5e9" },
  manual: { label: "MANUAL", color: "#7c4dff" },
  walkin: { label: "WALK-IN", color: "#1bab88" },
};

const SourceTag = ({ source }) => {
  const cfg = SOURCE_TAGS[source] || {
    label: String(source || "").toUpperCase(),
    color: "#7c4dff",
  };
  return (
    <span
      className="anx-tag"
      style={{ background: `${cfg.color}22`, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
};

const TOOLTIP_STYLE = {
  fontFamily: '"Mulish", sans-serif',
  fontSize: 13,
  borderRadius: 9,
  border: "1px solid #e5dfd3",
  boxShadow: "0 10px 26px rgba(0,0,0,.1)",
  background: "#fff",
};

const AXIS_TICK = { fontSize: 11, fill: "#9a958c" };

/* tour-log date presets (legacy setDateRangePreset options) */
const LOG_PRESETS = [
  ["week", "This Week"],
  ["month", "This Month"],
  ["3months", "3 Months"],
  ["6months", "6 Months"],
  ["12months", "12 Months"],
  ["ytd", "Year to Date"],
  ["all", "All Time"],
];

/* ----------------------------------------------------------- component -- */

const RAdminAnalytics = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();
  const isInitialMount = useRef(true);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Date range — default last 30 days (legacy default)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return isoDate(date);
  });
  const [endDate, setEndDate] = useState(() => isoDate(new Date()));
  // which quick preset is visually active ("30" | "7" | null) — design state
  const [activePreset, setActivePreset] = useState("30");

  // Analytics data
  const [bookingsData, setBookingsData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    total_bookings: 0,
    total_revenue: 0,
    total_guests: 0,
    average_booking_value: 0,
  });
  const [tourDistribution, setTourDistribution] = useState([]);

  // Tour log
  const [tourLog, setTourLog] = useState([]);
  const [loadingTourLog, setLoadingTourLog] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all"); // all | oasis | noble | manual
  const [sourceStats, setSourceStats] = useState({
    oasis: { total_entries: 0, total_guests: 0 },
    noble: { total_entries: 0, total_guests: 0 },
    manual: { total_entries: 0, total_guests: 0 },
  });

  // Export dropdowns (mutually exclusive, like the legacy page)
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showPdfDropdown, setShowPdfDropdown] = useState(false);

  /* ------------------------------------------------------------ fetches -- */

  const processBookingsData = (bookings) => {
    const bookingsByDate = {};
    const revenueByDate = {};
    let totalRevenue = 0;
    let totalGuests = 0;
    const timeDistribution = { "10:00": 0, "14:00": 0, "16:00": 0 };

    bookings.forEach((booking) => {
      const date = booking.tour_date;

      bookingsByDate[date] = (bookingsByDate[date] || 0) + 1;

      const revenue = booking.total_amount || 0;
      revenueByDate[date] = (revenueByDate[date] || 0) + revenue;
      totalRevenue += revenue;

      totalGuests += booking.guest_count || 0;

      if (
        booking.tour_time &&
        Object.prototype.hasOwnProperty.call(timeDistribution, booking.tour_time)
      ) {
        timeDistribution[booking.tour_time] += 1;
      }
    });

    const bookingsChartData = Object.entries(bookingsByDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({
        date: new Date(date).toLocaleDateString("en-AU", {
          month: "short",
          day: "numeric",
        }),
        bookings: count,
      }));

    const revenueChartData = Object.entries(revenueByDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, amount]) => ({
        date: new Date(date).toLocaleDateString("en-AU", {
          month: "short",
          day: "numeric",
        }),
        revenue: amount,
      }));

    setBookingsData(bookingsChartData);
    setRevenueData(revenueChartData);
    setTourDistribution([
      { name: "10:00 AM", value: timeDistribution["10:00"], color: DIST_COLORS["10:00"] },
      { name: "2:00 PM", value: timeDistribution["14:00"], color: DIST_COLORS["14:00"] },
      { name: "4:00 PM", value: timeDistribution["16:00"], color: DIST_COLORS["16:00"] },
    ]);
    setSummaryStats({
      total_bookings: bookings.length,
      total_revenue: totalRevenue,
      total_guests: totalGuests,
      average_booking_value:
        bookings.length > 0 ? totalRevenue / bookings.length : 0,
    });
  };

  const fetchAnalyticsData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setRefreshing(!showLoading);

      const cacheBuster = `_t=${Date.now()}`;
      const response = await adminFetch(
        `/api/admin/bookings?start_date=${startDate}&end_date=${endDate}&${cacheBuster}`,
        { navigate, headers: NO_CACHE_HEADERS, cache: "no-store" }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch bookings data");
      }

      const result = await response.json();
      processBookingsData(result.bookings || []);
      setLastUpdated(new Date());
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      if (showLoading) setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTourLog = async () => {
    setLoadingTourLog(true);
    try {
      const cacheBuster = `_t=${Date.now()}`;
      let url = `/api/admin/tours/log?start_date=${startDate}&end_date=${endDate}&${cacheBuster}`;
      if (sourceFilter !== "all") {
        url += `&source=${sourceFilter}`;
      }

      const response = await adminFetch(url, {
        navigate,
        headers: { ...NO_CACHE_HEADERS, Expires: "0" },
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch tour log");
      }

      const data = await response.json();
      setTourLog(data.tour_log || []);
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      setLoadingTourLog(false);
    }
  };

  const fetchSourceStats = async () => {
    try {
      const cacheBuster = `_t=${Date.now()}`;
      const response = await adminFetch(
        `/api/admin/manual-tours/analytics?start_date=${startDate}&end_date=${endDate}&${cacheBuster}`,
        { navigate, headers: { "Cache-Control": "no-cache" }, cache: "no-store" }
      );

      if (response.ok) {
        const data = await response.json();
        // Backend returns stats under 'by_source' key
        setSourceStats(data.by_source || data);
      }
    } catch (err) {
      // legacy behaviour: silent (console only), no user-facing error
      if (!err.isAuthError) console.error("Error fetching source stats:", err);
    }
  };

  /* ------------------------------------------------------------ effects -- */

  // Auto-refresh every 30 seconds for real-time data (silent)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAnalyticsData(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [startDate, endDate]);

  // Full data load on mount + whenever the date range changes
  useEffect(() => {
    const showLoading = isInitialMount.current;
    fetchAnalyticsData(showLoading);
    fetchTourLog();
    fetchSourceStats();
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [startDate, endDate]);

  // Re-fetch tour log when source filter changes (no full page loading)
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchTourLog();
    }
  }, [sourceFilter]);

  // Tour time distribution from the tour log (online + walk-in guests only)
  useEffect(() => {
    if (tourLog.length > 0) {
      const timeDistribution = { "10:00": 0, "14:00": 0, "16:00": 0 };
      tourLog.forEach((log) => {
        if (
          Object.prototype.hasOwnProperty.call(timeDistribution, log.time)
        ) {
          timeDistribution[log.time] +=
            (log.online_count || 0) + (log.walkin_count || 0);
        }
      });
      setTourDistribution([
        { name: "10:00 AM", value: timeDistribution["10:00"], color: DIST_COLORS["10:00"] },
        { name: "2:00 PM", value: timeDistribution["14:00"], color: DIST_COLORS["14:00"] },
        { name: "4:00 PM", value: timeDistribution["16:00"], color: DIST_COLORS["16:00"] },
      ]);
    }
  }, [tourLog]);

  /* ----------------------------------------------------------- handlers -- */

  const applyQuickRange = (days) => {
    const date = new Date();
    setEndDate(isoDate(date));
    date.setDate(date.getDate() - days);
    setStartDate(isoDate(date));
    setActivePreset(String(days));
  };

  const setDateRangePreset = (preset) => {
    const today = new Date();
    let start = new Date();

    switch (preset) {
      case "week":
        start.setDate(today.getDate() - 7);
        break;
      case "month":
        start.setDate(today.getDate() - 30);
        break;
      case "3months":
        start.setMonth(today.getMonth() - 3);
        break;
      case "6months":
        start.setMonth(today.getMonth() - 6);
        break;
      case "12months":
        start.setMonth(today.getMonth() - 12);
        break;
      case "ytd":
        start = new Date(today.getFullYear(), 0, 1);
        break;
      case "all":
        start = new Date(2020, 0, 1); // very early date
        break;
      default:
        start.setDate(today.getDate() - 30);
    }

    setStartDate(isoDate(start));
    setEndDate(isoDate(today));
    setActivePreset(preset === "month" ? "30" : preset === "week" ? "7" : null);
  };

  /* summary CSV — client-side, same rows/filename as the legacy export */
  const handleExportData = () => {
    const csvData = [
      ["Date Range", `${startDate} to ${endDate}`],
      ["Total Bookings", summaryStats.total_bookings],
      ["Total Revenue", `$${summaryStats.total_revenue.toFixed(2)}`],
      ["Total Guests", summaryStats.total_guests],
      ["Average Booking Value", `$${summaryStats.average_booking_value.toFixed(2)}`],
    ]
      .map((row) => row.join(","))
      .join("\n");

    const filename = `analytics_${startDate}_to_${endDate}.csv`;
    triggerDownload(new Blob([csvData], { type: "text/csv" }), filename);
    toast.success(`⤓ Exporting ${filename}`);
  };

  /* tour-log CSV — client-side, with per-source filtering (legacy logic) */
  const handleExportTourLogCSV = (source = "all") => {
    let filteredLog = tourLog;
    if (source !== "all") {
      filteredLog = tourLog
        .filter((log) =>
          log.manual_tours.some((mt) => mt.tour_source === source)
        )
        .map((log) => ({
          ...log,
          manual_tours: log.manual_tours.filter(
            (mt) => mt.tour_source === source
          ),
          manual_count: log.manual_tours
            .filter((mt) => mt.tour_source === source)
            .reduce((sum, mt) => sum + mt.guest_count, 0),
        }));
    }

    const headers = [
      "Date",
      "Time",
      "Source",
      "Online",
      "Walk-in",
      "Manual",
      "Total",
      "Staff Assigned",
      "Notes",
    ];

    const rows = filteredLog.map((log) => {
      const manualNotes = log.manual_tours
        .map((mt) => {
          const parts = [];
          if (mt.tour_source) parts.push(`[${mt.tour_source.toUpperCase()}]`);
          if (mt.staff_member) parts.push(`Staff: ${mt.staff_member}`);
          if (mt.notes) parts.push(mt.notes);
          return parts.join(" - ");
        })
        .filter((note) => note)
        .join("; ");

      return [
        formatDateDisplay(log.date),
        log.time_display,
        source === "all"
          ? "All"
          : source.charAt(0).toUpperCase() + source.slice(1),
        log.online_count,
        log.walkin_count,
        log.manual_count,
        log.total_count + log.manual_count,
        log.staff_member || "-",
        `"${manualNotes || "-"}"`, // quoted to handle commas
      ];
    });

    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join(
      "\n"
    );

    const sourceSuffix = source !== "all" ? `_${source}` : "";
    const filename = `tour_log${sourceSuffix}_${startDate}_to_${endDate}.csv`;
    triggerDownload(new Blob([csvContent], { type: "text/csv" }), filename);
    setShowExportDropdown(false);
    toast.success(`⤓ Exporting ${filename}`);
  };

  /* tour-log PDF — server-generated blob (legacy endpoint + filename) */
  const handleExportTourLogPDF = async (source = "all") => {
    try {
      const cacheBuster = `_t=${Date.now()}`;
      let apiUrl = `/api/admin/tours/log/export-pdf?start_date=${startDate}&end_date=${endDate}&${cacheBuster}`;
      if (source !== "all") {
        apiUrl += `&source=${source}`;
      }

      const response = await adminFetch(apiUrl, {
        navigate,
        headers: NO_CACHE_HEADERS,
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Failed to export PDF");
      }

      const blob = await response.blob();
      const sourceSuffix = source !== "all" ? `_${source}` : "";
      const filename = `tour_log${sourceSuffix}_${startDate}_to_${endDate}.pdf`;
      triggerDownload(blob, filename);
      setShowPdfDropdown(false);
      toast.success(`⤓ Exporting ${filename}`);
    } catch (err) {
      if (!err.isAuthError)
        toast.error(`Failed to export PDF: ${err.message}`);
    }
  };

  /* ------------------------------------------------------- derived data -- */

  const distTotal = tourDistribution.reduce((sum, item) => sum + item.value, 0);

  const peakTime =
    tourDistribution.length > 0
      ? tourDistribution.reduce((max, item) =>
          item.value > max.value ? item : max
        ).name
      : "N/A";

  const avgGuestsPerBooking =
    summaryStats.total_bookings > 0
      ? (summaryStats.total_guests / summaryStats.total_bookings).toFixed(1)
      : "0";

  /* rows: filter (source filter → manual rows only), newest first */
  const visibleLog = [...tourLog]
    .filter((log) => {
      if (sourceFilter !== "all") {
        return log.manual_count > 0;
      }
      return log.total_count > 0 || log.manual_count > 0;
    })
    .sort((a, b) => {
      if (a.date !== b.date) {
        return b.date.localeCompare(a.date);
      }
      return b.time.localeCompare(a.time);
    });

  const totalToursCount = tourLog.filter(
    (log) => log.total_count > 0 || log.manual_count > 0
  ).length;

  const walkinsTotal = tourLog.reduce(
    (sum, log) => sum + (log.walkin_count || 0),
    0
  );

  /* per-row source tags (single tag of the active filter when filtered —
     mirroring the legacy Source column behaviour) */
  const rowSourceTags = (log) => {
    if (sourceFilter !== "all") return [sourceFilter];
    const tags = [];
    if ((log.online_count || 0) > 0) tags.push("online");
    const manualSources = [
      ...new Set(
        (log.manual_tours || []).map((mt) => mt.tour_source || "manual")
      ),
    ];
    tags.push(...manualSources);
    if (tags.length === 0 && (log.walkin_count || 0) > 0) tags.push("walkin");
    return tags;
  };

  const tableNotes = (log) =>
    log.manual_tours
      .map((mt) => {
        const parts = [];
        if (mt.staff_member) parts.push(`Staff: ${mt.staff_member}`);
        if (mt.notes) parts.push(mt.notes);
        return parts.join(" - ");
      })
      .filter((note) => note)
      .join("; ");

  /* KPI sub-label: "Last N days" when the range ends today, else the range */
  const rangeLabel = (() => {
    if (endDate === isoDate(new Date())) {
      const days = Math.round(
        (new Date(endDate) - new Date(startDate)) / 86400000
      );
      if (days > 0) return `Last ${days} days`;
    }
    return "Selected range";
  })();

  const kpis = [
    {
      label: "Total Bookings",
      value: summaryStats.total_bookings.toLocaleString("en-AU"),
      icon: "adm-stat-icon--teal",
      subCls: "adm-stat-sub--teal",
      sub: rangeLabel,
    },
    {
      label: "Total Revenue",
      value: formatCurrencyWhole(summaryStats.total_revenue),
      icon: "adm-stat-icon--blue",
      subCls: "adm-stat-sub--blue",
      sub: rangeLabel,
    },
    {
      label: "Total Guests",
      value: summaryStats.total_guests.toLocaleString("en-AU"),
      icon: "adm-stat-icon--ember",
      subCls: "adm-stat-sub--ember",
      sub: rangeLabel,
    },
    {
      label: "Avg Booking Value",
      value: formatCurrency(summaryStats.average_booking_value),
      icon: "adm-stat-icon--purple",
      subCls: "anx-sub--purple",
      sub: "Per booking",
    },
  ];

  /* --------------------------------------------------------------- view -- */

  return (
    <AdminShell
      active="analytics"
      pageClass="pg-admin-analytics"
      eyebrow="Overview"
      title="Analytics & Reporting"
      subtitle={<span className="anx-autorefresh">● auto-refresh 30s</span>}
      actions={
        <>
          <button
            type="button"
            className="adm-btn adm-btn--ghost"
            onClick={handleExportData}
          >
            ⤓ Export CSV
          </button>
          <button
            type="button"
            className="adm-iconbtn"
            onClick={() => fetchAnalyticsData()}
            disabled={refreshing}
            title="Refresh"
            aria-label="Refresh"
          >
            <svg
              className={refreshing ? "anx-spin" : undefined}
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="#2a2018"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 11a8 8 0 10-2 6" />
              <path d="M20 4v5h-5" />
            </svg>
          </button>
        </>
      }
    >
      {loading ? (
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading analytics...
        </div>
      ) : (
        <>
          {/* Date range toolbar */}
          <div className="anx-toolbar">
            <div className="anx-range">
              <input
                type="date"
                className="anx-date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActivePreset(null);
                }}
              />
              <span className="anx-arrow">→</span>
              <input
                type="date"
                className="anx-date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActivePreset(null);
                }}
              />
              <button
                type="button"
                className={
                  "anx-rangebtn" + (activePreset === "30" ? " is-active" : "")
                }
                onClick={() => applyQuickRange(30)}
              >
                Last 30 Days
              </button>
              <button
                type="button"
                className={
                  "anx-rangebtn" + (activePreset === "7" ? " is-active" : "")
                }
                onClick={() => applyQuickRange(7)}
              >
                Last 7 Days
              </button>
              {refreshing && <span className="anx-updating">Updating...</span>}
            </div>
          </div>

          {/* KPI tiles */}
          <div className="adm-grid4 anx-kpis">
            {kpis.map((kpi) => (
              <div className="adm-stat" key={kpi.label}>
                <div className="adm-stat-top">
                  <span className="adm-stat-label">{kpi.label}</span>
                  <span className={`adm-stat-icon ${kpi.icon}`} />
                </div>
                <div className="adm-stat-value">{kpi.value}</div>
                <div className={`adm-stat-sub ${kpi.subCls}`}>{kpi.sub}</div>
              </div>
            ))}
          </div>

          {/* Charts row 1 */}
          <div className="anx-grid-charts">
            <div className="adm-card">
              <div className="anx-chart-title">Bookings over time</div>
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart
                  data={bookingsData}
                  margin={{ top: 6, right: 8, bottom: 0, left: 0 }}
                >
                  <CartesianGrid stroke="#f0ece3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={AXIS_TICK}
                    tickLine={false}
                    axisLine={{ stroke: "#e5dfd3" }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={AXIS_TICK}
                    tickLine={false}
                    axisLine={false}
                    width={32}
                  />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area
                    type="monotone"
                    dataKey="bookings"
                    name="Bookings"
                    stroke="#1bab88"
                    strokeWidth={2.5}
                    fill="#1bab88"
                    fillOpacity={0.08}
                    dot={false}
                    activeDot={{ r: 4, fill: "#1bab88", strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="adm-card">
              <div className="anx-chart-title">Revenue over time</div>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart
                  data={revenueData}
                  margin={{ top: 6, right: 8, bottom: 0, left: 0 }}
                >
                  <defs>
                    <linearGradient id="anxRevGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1bab88" />
                      <stop offset="100%" stopColor="#0f7d62" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#f0ece3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={AXIS_TICK}
                    tickLine={false}
                    axisLine={{ stroke: "#e5dfd3" }}
                  />
                  <YAxis
                    tick={AXIS_TICK}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(value) => `$${Number(value).toFixed(2)}`}
                    cursor={{ fill: "#f7f5f1" }}
                  />
                  <Bar
                    dataKey="revenue"
                    name="Revenue"
                    fill="url(#anxRevGrad)"
                    radius={[5, 5, 0, 0]}
                    maxBarSize={26}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts row 2 — distribution + performance metrics */}
          <div className="anx-grid-dist">
            <div className="adm-card">
              <div className="anx-chart-title">Tour time distribution</div>
              <div className="anx-donut-row">
                {distTotal > 0 ? (
                  <div className="anx-donut">
                    <PieChart width={130} height={130}>
                      <Pie
                        data={tourDistribution}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={65}
                        stroke="none"
                      >
                        {tourDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                    </PieChart>
                  </div>
                ) : (
                  <div className="anx-donut-empty" aria-hidden="true" />
                )}
                <div className="anx-legend">
                  {tourDistribution.map((item) => (
                    <div className="anx-legend-row" key={item.name}>
                      <span
                        className="anx-swatch"
                        style={{ background: item.color }}
                      />
                      <span className="anx-legend-name">{item.name}</span>
                      <span className="anx-legend-val">
                        {distTotal > 0
                          ? Math.round((item.value / distTotal) * 100)
                          : 0}
                        %
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="adm-card">
              <div className="anx-chart-title anx-chart-title--tight">
                Performance metrics
              </div>
              <div className="anx-metric">
                <span className="anx-metric-label">Booking rate (online)</span>
                <span className="anx-metric-val anx-mv--teal">
                  {summaryStats.total_bookings > 0 ? "100%" : "0%"}
                </span>
              </div>
              <div className="anx-metric">
                <span className="anx-metric-label">Revenue growth</span>
                <span className="anx-metric-val anx-mv--blue">
                  {formatCurrency(summaryStats.total_revenue)}
                </span>
              </div>
              <div className="anx-metric">
                <span className="anx-metric-label">Avg guests / booking</span>
                <span className="anx-metric-val anx-mv--ember">
                  {avgGuestsPerBooking}
                </span>
              </div>
              <div className="anx-metric">
                <span className="anx-metric-label">Peak time</span>
                <span className="anx-metric-val anx-mv--purple">{peakTime}</span>
              </div>
            </div>
          </div>

          {/* Comprehensive tour log */}
          <div className="adm-card">
            <div className="anx-log-head">
              <div className="anx-log-title">Comprehensive tour log</div>
              <div className="anx-log-actions">
                <select
                  className="anx-log-select"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  aria-label="Tour source"
                >
                  <option value="all">All sources</option>
                  <option value="oasis">Oasis</option>
                  <option value="noble">Noble</option>
                  <option value="manual">Manual</option>
                </select>

                <div className="anx-dd">
                  <button
                    type="button"
                    className="adm-btn adm-btn--ghost"
                    disabled={loadingTourLog}
                    onClick={() => {
                      setShowExportDropdown((v) => !v);
                      setShowPdfDropdown(false);
                    }}
                  >
                    ⤓ CSV
                  </button>
                  {showExportDropdown && (
                    <div className="anx-dd-menu">
                      <button
                        type="button"
                        onClick={() => handleExportTourLogCSV("all")}
                      >
                        All Tours
                      </button>
                      <button
                        type="button"
                        className="anx-dd--oasis"
                        onClick={() => handleExportTourLogCSV("oasis")}
                      >
                        Oasis Tours Only
                      </button>
                      <button
                        type="button"
                        className="anx-dd--noble"
                        onClick={() => handleExportTourLogCSV("noble")}
                      >
                        Noble Tours Only
                      </button>
                      <button
                        type="button"
                        className="anx-dd--manual"
                        onClick={() => handleExportTourLogCSV("manual")}
                      >
                        Manual Tours Only
                      </button>
                    </div>
                  )}
                </div>

                <div className="anx-dd">
                  <button
                    type="button"
                    className="adm-btn adm-btn--ghost"
                    disabled={loadingTourLog}
                    onClick={() => {
                      setShowPdfDropdown((v) => !v);
                      setShowExportDropdown(false);
                    }}
                  >
                    ⤓ PDF
                  </button>
                  {showPdfDropdown && (
                    <div className="anx-dd-menu">
                      <button
                        type="button"
                        onClick={() => handleExportTourLogPDF("all")}
                      >
                        All Tours
                      </button>
                      <button
                        type="button"
                        className="anx-dd--oasis"
                        onClick={() => handleExportTourLogPDF("oasis")}
                      >
                        Oasis Tours Only
                      </button>
                      <button
                        type="button"
                        className="anx-dd--noble"
                        onClick={() => handleExportTourLogPDF("noble")}
                      >
                        Noble Tours Only
                      </button>
                      <button
                        type="button"
                        className="anx-dd--manual"
                        onClick={() => handleExportTourLogPDF("manual")}
                      >
                        Manual Tours Only
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick date-range presets (legacy) */}
            <div className="anx-presets">
              <span className="anx-presets-label">Date range</span>
              {LOG_PRESETS.map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  className="anx-preset"
                  onClick={() => setDateRangePreset(key)}
                >
                  {label}
                </button>
              ))}
              {loadingTourLog && (
                <span className="anx-updating">Loading...</span>
              )}
            </div>

            {/* Summary tiles */}
            <div className="adm-grid5 anx-log-tiles">
              <div className="anx-tile">
                <div className="anx-tile-label">Total Tours</div>
                <div className="anx-tile-value">{totalToursCount}</div>
              </div>
              <div className="anx-tile">
                <div className="anx-tile-label">Oasis</div>
                <div className="anx-tile-value anx-tile-value--oasis">
                  {(sourceStats.oasis && sourceStats.oasis.total_guests) || 0}
                </div>
                <div className="anx-tile-sub">
                  {(sourceStats.oasis && sourceStats.oasis.total_entries) || 0}{" "}
                  entries
                </div>
              </div>
              <div className="anx-tile">
                <div className="anx-tile-label">Noble</div>
                <div className="anx-tile-value anx-tile-value--noble">
                  {(sourceStats.noble && sourceStats.noble.total_guests) || 0}
                </div>
                <div className="anx-tile-sub">
                  {(sourceStats.noble && sourceStats.noble.total_entries) || 0}{" "}
                  entries
                </div>
              </div>
              <div className="anx-tile">
                <div className="anx-tile-label">Manual</div>
                <div className="anx-tile-value anx-tile-value--manual">
                  {(sourceStats.manual && sourceStats.manual.total_guests) || 0}
                </div>
                <div className="anx-tile-sub">
                  {(sourceStats.manual && sourceStats.manual.total_entries) ||
                    0}{" "}
                  entries
                </div>
              </div>
              <div className="anx-tile">
                <div className="anx-tile-label">Walk-ins</div>
                <div className="anx-tile-value anx-tile-value--walkin">
                  {walkinsTotal}
                </div>
              </div>
            </div>

            {/* Log table */}
            {loadingTourLog ? (
              <div className="adm-loading">
                <div className="adm-spinner" />
                Loading tour log...
              </div>
            ) : visibleLog.length === 0 ? (
              <div className="adm-empty">
                <div className="adm-empty-icon">📅</div>
                <div className="adm-empty-title">No tour data</div>
                <div className="adm-empty-sub">
                  No tour data available for this date range
                </div>
              </div>
            ) : (
              <div className="anx-tablewrap">
                <table className="anx-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Source</th>
                      <th className="anx-c">Online</th>
                      <th className="anx-c">Walk-in</th>
                      <th className="anx-c">Manual</th>
                      <th className="anx-c">Total</th>
                      <th>Staff</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLog.map((log, index) => {
                      const notes = tableNotes(log);
                      const tags = rowSourceTags(log);
                      return (
                        <tr key={`${log.date}-${log.time}-${index}`}>
                          <td className="anx-td-date">
                            {formatDateDisplay(log.date)}
                          </td>
                          <td className="anx-td-time">{log.time_display}</td>
                          <td>
                            {tags.length > 0 ? (
                              <span className="anx-tags">
                                {tags.map((tag) => (
                                  <SourceTag key={tag} source={tag} />
                                ))}
                              </span>
                            ) : (
                              <span className="anx-td-staff">—</span>
                            )}
                          </td>
                          <td className="anx-c anx-td-online">
                            {log.online_count}
                          </td>
                          <td className="anx-c anx-td-walkin">
                            {log.walkin_count}
                          </td>
                          <td className="anx-c anx-td-manual">
                            {log.manual_count}
                          </td>
                          <td className="anx-c anx-td-total">
                            {log.total_count + log.manual_count}
                          </td>
                          <td
                            className={
                              log.staff_member
                                ? "anx-td-staffname"
                                : "anx-td-staff"
                            }
                          >
                            {log.staff_member || "-"}
                          </td>
                          <td className="anx-td-notes" title={notes || undefined}>
                            {notes || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="anx-foot">
            Auto-refreshes every 30 seconds • Last updated:{" "}
            {(lastUpdated || new Date()).toLocaleTimeString("en-AU")}
          </div>
        </>
      )}
    </AdminShell>
  );
};

export default RAdminAnalytics;
